import React, { useState } from 'react';
import {
  UserCheck,
  Calendar,
  CheckCircle2,
  XCircle,
  Plus,
  Download,
  Eye,
  X,
  Users,
} from 'lucide-react';
import { AttendanceSessionLog, AdminStudent, AdminRole } from '../types';
import { Badge, Button, Input, Tooltip } from '../../components/core';

interface AttendanceTrackingProps {
  logs: AttendanceSessionLog[];
  students: AdminStudent[];
  currentRole: AdminRole;
  onAddAttendanceLog: (log: AttendanceSessionLog) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const AttendanceTracking: React.FC<AttendanceTrackingProps> = ({
  logs,
  students,
  currentRole,
  onAddAttendanceLog,
  addToast,
}) => {
  const [selectedLog, setSelectedLog] = useState<AttendanceSessionLog | null>(null);
  const [isMarkModalOpen, setIsMarkModalOpen] = useState(false);

  const isAuditor = currentRole === 'Auditor';

  // New session marking form state
  const [sessionDate, setSessionDate] = useState('2026-07-23');
  const [selectedLevel, setSelectedLevel] = useState('Level 1 - Prarambhik');
  const [selectedBatch, setSelectedBatch] = useState('Batch A - Weekend Morning');

  // Student Attendance toggle Map
  const [attendanceMap, setAttendanceMap] = useState<Record<string, boolean>>({});

  const levelStudents = students.filter((s) => s.level === selectedLevel && s.batch === selectedBatch);

  const openNewAttendanceModal = () => {
    if (isAuditor) {
      addToast('warning', 'Access Restricted', 'View-Only Auditors cannot log attendance.');
      return;
    }
    // Initialize all as present by default
    const initialMap: Record<string, boolean> = {};
    levelStudents.forEach((s) => {
      initialMap[s.id] = true;
    });
    setAttendanceMap(initialMap);
    setIsMarkModalOpen(true);
  };

  const toggleStudentAttendance = (stuId: string) => {
    setAttendanceMap((prev) => ({
      ...prev,
      [stuId]: !prev[stuId],
    }));
  };

  const handleSaveAttendance = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    const records = levelStudents.map((s) => ({
      studentId: s.id,
      studentName: `${s.firstName} ${s.surname}`,
      status: (attendanceMap[s.id] ? 'Present' : 'Absent') as 'Present' | 'Absent' | 'Late',
    }));

    const presentCount = records.filter((r) => r.status === 'Present').length;
    const totalEnrolled = records.length;
    const attendanceRate = totalEnrolled > 0 ? Math.round((presentCount / totalEnrolled) * 100) : 100;

    const newLog: AttendanceSessionLog = {
      id: `ATT-${Math.floor(1000 + Math.random() * 9000)}`,
      sessionDate,
      level: selectedLevel,
      batch: selectedBatch,
      classId: 'CLS-REG',
      totalEnrolled,
      presentCount,
      absentCount: totalEnrolled - presentCount,
      attendanceRate,
      status: 'Completed',
      records,
    };

    onAddAttendanceLog(newLog);
    addToast('success', 'Attendance Saved', `Logged attendance session for ${selectedLevel} (${presentCount}/${totalEnrolled} present)`);
    setIsMarkModalOpen(false);
  };

  const handleExportCSV = () => {
    const csvHeader = 'Session ID,Date,Level,Batch,Enrolled,Present,Absent,Rate %\n';
    const csvRows = logs
      .map((l) => `${l.id},${l.sessionDate},"${l.level}","${l.batch}",${l.totalEnrolled},${l.presentCount},${l.absentCount},${l.attendanceRate}%`)
      .join('\n');

    const blob = new Blob([csvHeader + csvRows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Pathshala_Attendance_Summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    addToast('info', 'CSV Download Initiated', `Exported ${logs.length} session attendance records.`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Attendance Log & Audit Console</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" leftIcon={Download} onClick={handleExportCSV}>
            Export CSV Report
          </Button>
          {!isAuditor && (
            <Button leftIcon={Plus} onClick={openNewAttendanceModal}>
              Mark Session Attendance
            </Button>
          )}
        </div>
      </div>

      {/* Session Logs History Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
            <thead className="border-b border-[var(--gv-border)] bg-[var(--gv-surface)] font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
              <tr>
                <th className="py-2.5 px-4 font-bold">Log ID</th>
                <th className="py-2.5 px-4 font-bold">Session Date</th>
                <th className="py-2.5 px-4 font-bold">Level & Batch Slot</th>
                <th className="py-2.5 px-4 text-center font-bold">Enrolled</th>
                <th className="py-2.5 px-4 text-center font-bold">Present / Absent</th>
                <th className="py-2.5 px-4 text-center font-bold">Attendance %</th>
                <th className="py-2.5 px-4 text-right font-bold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 font-medium bg-white">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-[var(--gv-surface)]/80 transition-colors">
                  <td className="py-3 px-4 font-mono font-bold text-[var(--gv-brand)]">{log.id}</td>
                  <td className="py-3 px-4 font-mono text-[var(--gv-ink)]">{log.sessionDate}</td>
                  <td className="py-3 px-4">
                    <div className="font-bold text-[var(--gv-ink)]">{log.level}</div>
                    <div className="text-[10px] text-[var(--gv-ink-muted)]">{log.batch}</div>
                  </td>
                  <td className="py-3 px-4 text-center font-mono font-bold text-[var(--gv-ink-muted)]">{log.totalEnrolled}</td>
                  <td className="py-3 px-4 text-center font-mono font-bold">
                    <span className="text-emerald-700">{log.presentCount} P</span> /{' '}
                    <span className="text-rose-700">{log.absentCount} A</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <Badge
                      variant={log.attendanceRate >= 85 ? 'success' : 'warning'}
                      className="font-mono normal-case tracking-normal"
                    >
                      {log.attendanceRate}%
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={Eye}
                      className="ml-auto"
                      onClick={() => setSelectedLog(log)}
                    >
                      View Roster
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Roster Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-lg w-full p-6 shadow-xl space-y-4 text-[var(--gv-ink)]">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <div>
                <h3 className="text-base font-bold text-[var(--gv-ink)]">Attendance Log Roster: {selectedLog.id}</h3>
                <p className="text-xs text-[var(--gv-ink-muted)] font-mono mt-0.5">{selectedLog.level} • {selectedLog.sessionDate}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setSelectedLog(null)}
              />
            </div>

            <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1 text-xs">
              {selectedLog.records.map((rec, i) => (
                <div key={i} className="p-3 bg-[var(--gv-surface)] rounded-lg border border-[var(--gv-border)] flex items-center justify-between">
                  <div className="font-bold text-[var(--gv-ink)]">{rec.studentName}</div>
                  <Badge variant={rec.status === 'Present' ? 'success' : 'danger'}>
                    {rec.status}
                  </Badge>
                </div>
              ))}
            </div>

            <div className="pt-2 flex justify-end">
              <Button variant="secondary" onClick={() => setSelectedLog(null)}>
                Close Roster
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mark New Session Modal */}
      {isMarkModalOpen && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-xl w-full p-6 shadow-xl space-y-4 text-[var(--gv-ink)] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">Mark Session Attendance Roster</h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsMarkModalOpen(false)}
              />
            </div>

            <form onSubmit={handleSaveAttendance} className="space-y-4 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <Input
                  label="Session Date"
                  type="date"
                  required
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                />
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Academic Level</label>
                  <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Level 1 - Prarambhik">Level 1 - Prarambhik</option>
                    <option value="Level 2 - Madhyamik">Level 2 - Madhyamik</option>
                    <option value="Level 3 - Shravak Junior">Level 3 - Shravak Junior</option>
                  </select>
                </div>
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Batch Slot</label>
                  <select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Batch A - Weekend Morning">Batch A - Weekend Morning</option>
                    <option value="Batch B - Weekend Evening">Batch B - Weekend Evening</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--gv-ink)] mb-1.5">
                  Student Present Checklist ({levelStudents.length} Students)
                </label>
                <div className="space-y-2 max-h-[250px] overflow-y-auto bg-[var(--gv-surface)] p-3 rounded-lg border border-[var(--gv-border)]">
                  {levelStudents.map((s) => {
                    const isPresent = attendanceMap[s.id] !== false;
                    return (
                      <div
                        key={s.id}
                        onClick={() => toggleStudentAttendance(s.id)}
                        className={`p-2.5 rounded-lg border flex items-center justify-between cursor-pointer transition-colors ${
                          isPresent ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-rose-50 border-rose-200 text-rose-900'
                        }`}
                      >
                        <div className="font-bold">{s.firstName} {s.surname}</div>
                        <span className="text-[10px] font-mono font-bold uppercase">{isPresent ? 'Present ✓' : 'Absent ✗'}</span>
                      </div>
                    );
                  })}
                  {levelStudents.length === 0 && (
                    <p className="text-center text-[var(--gv-ink-muted)] py-4 font-mono">No enrolled students found for this level & batch slot.</p>
                  )}
                </div>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsMarkModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Save Attendance Session
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
